'use client';

import { useState } from 'react';
import Image from 'next/image';
import { DOMNode } from 'html-react-parser';
import YouTubeStructuredData from '@/app/components/atomic/molecules/YoutubeStructuredData';

interface YouTubeEmbedProps {
    id: string;
    embedHost?: string;
    embedParams?: Record<string, string>;
}

// YouTube serves this exact 120x90 gray placeholder (HTTP 200) when a video has no maxresdefault thumbnail
const MAXRES_PLACEHOLDER_WIDTH = 120;

const YouTubeEmbed: React.FC<YouTubeEmbedProps> = ({ id, embedHost = 'www.youtube.com', embedParams }) => {
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);
    const [thumbnailUrl, setThumbnailUrl] = useState(`https://img.youtube.com/vi/${id}/maxresdefault.jpg`);
    const iframeSrc = `https://${embedHost}/embed/${id}?${new URLSearchParams({ ...embedParams, autoplay: '1' })}`;

    return (
        <div className="relative w-full aspect-video bg-black">
            {isVideoLoaded ? (
                <iframe
                    className="absolute inset-0 w-full h-full border-none"
                    src={iframeSrc}
                    title="YouTube video player"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                />
            ) : (
                <div
                    className="relative w-full h-full cursor-pointer"
                    onClick={() => setIsVideoLoaded(true)}
                >
                    <Image
                        src={thumbnailUrl}
                        alt="YouTube video thumbnail"
                        layout="fill"
                        objectFit="cover"
                        priority
                        onLoad={(event) => {
                            if (event.currentTarget.naturalWidth === MAXRES_PLACEHOLDER_WIDTH) {
                                setThumbnailUrl(`https://img.youtube.com/vi/${id}/hqdefault.jpg`);
                            }
                        }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white/90 rounded-full p-3 text-xl size-12 flex justify-center items-center">▶</div>
                    </div>
                </div>
            )}
        </div>
    );
};

const YOUTUBE_EMBED_HOSTS = new Set(['www.youtube.com', 'youtube.com', 'www.youtube-nocookie.com', 'youtube-nocookie.com']);
const YOUTUBE_EMBED_PATH_REGEX = /^\/embed\/([a-zA-Z0-9_-]+)$/;
// Params an editor may legitimately set on a pasted iframe (e.g. a trimmed start/end) that we want to preserve
const SUPPORTED_EMBED_PARAMS = ['start', 'end'];

interface ParsedYouTubeEmbed {
    id: string;
    host: string;
    params: Record<string, string>;
}

// Parses src as a URL (handles protocol-relative //host/... too) so a hostname like
// "notyoutube.com" or a query string containing "youtube.com/embed/" can't spoof the match
function parseYouTubeEmbed(src: string): ParsedYouTubeEmbed | undefined {
    try {
        const url = new URL(src, 'https://placeholder.invalid');
        if (!YOUTUBE_EMBED_HOSTS.has(url.hostname)) {
            return undefined;
        }
        const id = url.pathname.match(YOUTUBE_EMBED_PATH_REGEX)?.[1];
        if (!id) {
            return undefined;
        }
        const params: Record<string, string> = {};
        for (const key of SUPPORTED_EMBED_PARAMS) {
            const value = url.searchParams.get(key);
            if (value) {
                params[key] = value;
            }
        }
        return { id, host: url.hostname, params };
    } catch {
        return undefined;
    }
}

// Catches iframes pasted directly by editors (Gutenberg HTML block), bypassing the [youtube_embed] shortcode
export function processYouTubeIframe(domNode: DOMNode): React.ReactNode | undefined {
    if (domNode.type === 'tag' && domNode.name === 'iframe') {
        const src = domNode.attribs?.src || '';
        const parsed = parseYouTubeEmbed(src);
        if (parsed) {
            const title = domNode.attribs?.title;
            return (
                <>
                    <YouTubeStructuredData id={parsed.id} title={title} />
                    <YouTubeEmbed id={parsed.id} embedHost={parsed.host} embedParams={parsed.params} />
                </>
            );
        }
    }
    return undefined;
}

export default YouTubeEmbed;
